import { useNavigate, useParams } from '@ksyos/router';
import { useEffect, useState } from 'react';
import { ManagerAuthorization } from '@common/enums';
import { VatRateGroup } from '@common/enums/vatRateGroup';
import { formatString } from '@common/utils/formatString';
import { nonNullable } from '@common/utils/nonNullableTypeUtils';
import { getSelectedLanguage } from '@frontend/translate';
import { assertHasAuthorization, useAbortableFetch } from '@frontend/utils';
import type { IEpisodeCategory } from '../../../../common/interfaces/episodeCategory';
import type { ValidationErrors } from '../../../utils/validation';
import type { ISpecialism } from '../../interfaces/specialism';
import { ServiceCode } from '../../../../common/enums/serviceCode';
import { ServiceType } from '../../../../common/enums/serviceType';
import { ServiceZorgmailType } from '../../../../common/enums/serviceZorgmailType';
import { serviceAddEditSchema } from '../../../../common/validation/service';
import { getSpecialismSelectOptions } from '../../../utils/getSpecialismSelectOptions';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '../../../utils/validation';
import {
    CancelButton,
    Dropdown,
    EnumDropdown,
    FormContainer,
    FormGroup,
    FormHeader,
    FormLabel,
    Header,
    InputCheckbox,
    InputGroup,
    InputNumber,
    InputText,
    SaveSubmit,
} from '../../elements/common';
import { HorizontalFormGroup } from '../../elements/forms/HorizontalFormGroup';
import { OrganizationSelection } from '../../elements/OrganizationSelection';
import { FixedWidthTemplate } from '../../templates/FixedWidthTemplate';

interface IService {
    authorization?: IAuthorization;
    code: ServiceCode | null;
    contractFunding: boolean;
    customControlVersion: number | null;
    doNotDeclareSet: boolean;
    episodeCategoryId: string | null;
    isActive: boolean;
    isFundable: boolean;
    isStandaloneGraderNotAllowed: boolean;
    isSuppressNotifications: boolean;
    isSuppressZorgmail: boolean;
    ksyosHolterAnalystOrganizationId: string | null;
    ledgerNumber: string;
    name: string;
    noGeneralPractitionerInvolved: boolean;
    runFundingAfterEachActivity: boolean;
    sendNotificationOnCloseSubsequentService: boolean;
    sendZorgmailOnCloseSubsequentService: boolean;
    serviceType: ServiceType | null;
    specialismCode: string | null;
    supervisionPercentage: number | null;
    useNewFundingSystem: boolean;
    vatRateGroup: VatRateGroup;
    zorgmailType: ServiceZorgmailType;
}

interface IAuthorization {
    canUpdateSupervisionPercentage?: boolean;
}

export function ServiceAddEdit() {
    assertHasAuthorization(ManagerAuthorization.serviceAddEdit);
    const navigate = useNavigate();
    const { id } = useParams();
    const [specialisms, setSpecialisms] = useState<ISpecialism[]>([]);
    const [episodeCategories, setEpisodeCategories] = useState<IEpisodeCategory[]>([]);
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [isSaving, setIsSaving] = useState(false);
    const [service, setService] = useState<IService>({} as IService);
    const abortableFetch = useAbortableFetch();

    const isEditMode = Boolean(id);
    const language = getSelectedLanguage();

    useEffect(() => {
        initialize();
    }, []);

    useEffect(() => {
        if (isEditMode === false && service.useNewFundingSystem === false) {
            handleChange('isFundable', false);
            handleChange('runFundingAfterEachActivity', false);
        }
    }, [service.useNewFundingSystem]);

    useEffect(() => {
        if (isEditMode === false && service.isFundable === false) {
            handleChange('runFundingAfterEachActivity', false);
        }
    }, [service.isFundable]);

    return (
        <FixedWidthTemplate>
            <Header
                content={formatString(isEditMode ? language.common.editItem : language.common.addItem, [
                    language.service.service,
                ])}
            />

            <FormContainer onSubmit={handleSave}>
                <FormHeader>{language.service.details}</FormHeader>

                <FormGroup hasErrors={errors.has('name')}>
                    <FormLabel isRequired>{language.common.name}</FormLabel>
                    <InputGroup>
                        <InputText
                            error={errors.get('name')}
                            onChange={(value) => handleChange('name', value)}
                            onDoValidate={() => validateOne('name')}
                            placeholder={language.common.name}
                            value={service.name}
                        />
                    </InputGroup>
                </FormGroup>

                <FormGroup hasErrors={errors.has('code')}>
                    <FormLabel isRequired>{language.common.code}</FormLabel>
                    <InputGroup>
                        <EnumDropdown
                            error={errors.get('code')}
                            jsEnum={ServiceCode}
                            onChange={(value) => handleDropDownChange('code', value)}
                            value={service.code}
                        />
                    </InputGroup>
                </FormGroup>

                <FormGroup hasErrors={errors.has('ledgerNumber')}>
                    <FormLabel isRequired>{language.service.ledgerNumber}</FormLabel>
                    <InputGroup>
                        <InputText
                            error={errors.get('ledgerNumber')}
                            onChange={(value) => handleChange('ledgerNumber', value)}
                            onDoValidate={() => validateOne('ledgerNumber')}
                            placeholder={language.service.ledgerNumber}
                            value={service.ledgerNumber}
                        />
                    </InputGroup>
                </FormGroup>

                <FormHeader>
                    {language.service.service} &amp; {language.episodeCategory.episodeCategory}
                </FormHeader>
                <FormGroup hasErrors={errors.has('serviceType')}>
                    <FormLabel isRequired>{language.service.serviceType}</FormLabel>
                    <InputGroup>
                        <EnumDropdown
                            enumName={'serviceType'}
                            error={errors.get('serviceType')}
                            jsEnum={ServiceType}
                            onChange={(value) => handleDropDownChange('serviceType', value)}
                            value={service.serviceType}
                        />
                    </InputGroup>
                </FormGroup>

                <FormGroup hasErrors={errors.has('episodeCategoryId')}>
                    <FormLabel isRequired>{language.episodeCategory.episodeCategory}</FormLabel>
                    <InputGroup>
                        <Dropdown
                            error={errors.get('episodeCategoryId')}
                            onChange={(value) => handleDropDownChange('episodeCategoryId', value)}
                            selectOptions={getEpisodeCategorySelectOptions()}
                            value={service.episodeCategoryId}
                        />
                    </InputGroup>
                </FormGroup>

                <FormHeader>{language.common.settings}</FormHeader>
                {isExamination() && (
                    <HorizontalFormGroup hasErrors={errors.has('supervisionPercentage')}>
                        <FormLabel>{language.supervision.percentage}</FormLabel>
                        <InputNumber
                            disabled={!canUpdateSupervisionPercentage()}
                            error={errors.get('supervisionPercentage')}
                            onChange={(value) => handleChange('supervisionPercentage', value)}
                            onDoValidate={() => validateOne('supervisionPercentage')}
                            placeholder={language.supervision.percentage}
                            value={service.supervisionPercentage}
                        />
                    </HorizontalFormGroup>
                )}

                <FormGroup hasErrors={errors.has('isActive')}>
                    <FormLabel>{language.common.isActive}</FormLabel>
                    <InputGroup>
                        <InputCheckbox
                            checked={service.isActive}
                            error={errors.get('isActive')}
                            onChange={(value) => handleChange('isActive', value)}
                        />
                    </InputGroup>
                </FormGroup>

                <FormGroup hasErrors={errors.has('noGeneralPractitionerInvolved')}>
                    <FormLabel>{language.service.noGeneralPractitionerInvolved}</FormLabel>
                    <InputGroup>
                        <InputCheckbox
                            checked={service.noGeneralPractitionerInvolved}
                            error={errors.get('noGeneralPractitionerInvolved')}
                            onChange={(value) => handleChange('noGeneralPractitionerInvolved', value)}
                        />
                    </InputGroup>
                </FormGroup>

                <FormGroup hasErrors={errors.has('isSuppressNotifications')}>
                    <FormLabel>{language.service.isSuppressNotifications}</FormLabel>
                    <InputGroup>
                        <InputCheckbox
                            checked={service.isSuppressNotifications}
                            error={errors.get('isSuppressNotifications')}
                            onChange={(value) => handleChange('isSuppressNotifications', value)}
                        />
                    </InputGroup>
                </FormGroup>

                <FormGroup hasErrors={errors.has('sendNotificationOnCloseSubsequentService')}>
                    <FormLabel tooltipText={language.service.notificationOnCloseSubsequentServiceTooltip}>
                        {language.service.notificationOnCloseSubsequentService}
                    </FormLabel>
                    <InputGroup>
                        <InputCheckbox
                            checked={service.sendNotificationOnCloseSubsequentService}
                            error={errors.get('sendNotificationOnCloseSubsequentService')}
                            onChange={(value) => handleChange('sendNotificationOnCloseSubsequentService', value)}
                        />
                    </InputGroup>
                </FormGroup>

                <FormGroup hasErrors={errors.has('isSuppressZorgmail')}>
                    <FormLabel>{language.service.isSuppressZorgmail}</FormLabel>
                    <InputGroup>
                        <InputCheckbox
                            checked={service.isSuppressZorgmail}
                            error={errors.get('isSuppressZorgmail')}
                            onChange={(value) => handleChange('isSuppressZorgmail', value)}
                        />
                    </InputGroup>
                </FormGroup>

                <FormGroup hasErrors={errors.has('sendZorgmailOnCloseSubsequentService')}>
                    <FormLabel tooltipText={language.service.sendZorgmailOnCloseSubsequentServiceToolTip}>
                        {language.service.sendZorgmailOnCloseSubsequentService}
                    </FormLabel>
                    <InputGroup>
                        <InputCheckbox
                            checked={service.sendZorgmailOnCloseSubsequentService}
                            error={errors.get('sendZorgmailOnCloseSubsequentService')}
                            onChange={(value) => handleChange('sendZorgmailOnCloseSubsequentService', value)}
                        />
                    </InputGroup>
                </FormGroup>

                <FormGroup hasErrors={errors.has('zorgmailType')}>
                    <FormLabel isRequired>{language.service.zorgmailType}</FormLabel>
                    <InputGroup>
                        <EnumDropdown
                            error={errors.get('zorgmailType')}
                            jsEnum={ServiceZorgmailType}
                            noSelectPlaceHolder
                            onChange={(value) => handleDropDownChange('zorgmailType', nonNullable(value))}
                            value={service.zorgmailType}
                        />
                    </InputGroup>
                </FormGroup>

                {service && service.code === ServiceCode.TOO && (
                    <FormGroup hasErrors={errors.has('isStandaloneGraderNotAllowed')}>
                        <FormLabel tooltipText={language.service.isStandaloneGraderNotAllowedTooltip}>
                            {language.service.isStandaloneGraderNotAllowed}
                        </FormLabel>
                        <InputGroup>
                            <InputCheckbox
                                checked={service.isStandaloneGraderNotAllowed}
                                error={errors.get('isStandaloneGraderNotAllowed')}
                                onChange={(value) => handleChange('isStandaloneGraderNotAllowed', value)}
                            />
                        </InputGroup>
                    </FormGroup>
                )}

                <FormHeader>{language.common.funding}</FormHeader>

                <FormGroup hasErrors={errors.has('useNewFundingSystem')}>
                    <FormLabel tooltipText={language.service.useNewFundingSystemTooltip}>
                        {language.service.useNewFundingSystem}
                    </FormLabel>
                    <InputGroup>
                        <InputCheckbox
                            checked={service.useNewFundingSystem}
                            disabled={isEditMode}
                            error={errors.get('useNewFundingSystem')}
                            onChange={(value) => handleChange('useNewFundingSystem', value)}
                        />
                    </InputGroup>
                </FormGroup>

                <FormGroup hasErrors={errors.has('isFundable')}>
                    <FormLabel tooltipText={language.service.isFundableTooltip}>
                        {language.service.isFundable}
                    </FormLabel>
                    <InputGroup>
                        <InputCheckbox
                            checked={service.isFundable}
                            disabled={isEditMode || !service.useNewFundingSystem}
                            error={errors.get('isFundable')}
                            onChange={(value) => handleChange('isFundable', value)}
                        />
                    </InputGroup>
                </FormGroup>

                <FormGroup hasErrors={errors.has('runFundingAfterEachActivity')}>
                    <FormLabel tooltipText={language.service.runFundingAfterEachActivityTooltip}>
                        {language.service.runFundingAfterEachActivity}
                    </FormLabel>
                    <InputGroup>
                        <InputCheckbox
                            checked={service.runFundingAfterEachActivity}
                            disabled={isEditMode || !service.isFundable}
                            error={errors.get('runFundingAfterEachActivity')}
                            onChange={(value) => handleChange('runFundingAfterEachActivity', value)}
                        />
                    </InputGroup>
                </FormGroup>

                <FormGroup hasErrors={errors.has('doNotDeclareSet')}>
                    <FormLabel tooltipText={language.service.doNotDeclareSetTooltip}>
                        {language.service.doNotDeclareSet}
                    </FormLabel>
                    <InputGroup>
                        <InputCheckbox
                            checked={service.doNotDeclareSet}
                            disabled={isEditMode || service.useNewFundingSystem}
                            error={errors.get('doNotDeclareSet')}
                            onChange={(value) => handleChange('doNotDeclareSet', value)}
                        />
                    </InputGroup>
                </FormGroup>

                <FormGroup hasErrors={errors.has('contractFunding')}>
                    <FormLabel>{language.service.contractFunding}</FormLabel>
                    <InputGroup>
                        <InputCheckbox
                            checked={service.contractFunding}
                            error={errors.get('contractFunding')}
                            onChange={(value) => handleChange('contractFunding', value)}
                        />
                    </InputGroup>
                </FormGroup>

                <FormGroup hasErrors={errors.has('vatRateGroup')}>
                    <FormLabel isRequired>{language.common.vatRateGroup}</FormLabel>
                    <InputGroup>
                        <EnumDropdown
                            enumName={'VatTypes'}
                            error={errors.get('vatRateGroup')}
                            jsEnum={VatRateGroup}
                            noSelectPlaceHolder
                            onChange={(value) => handleDropDownChange('vatRateGroup', nonNullable(value))}
                            sortDirection={'DESC'}
                            value={service.vatRateGroup}
                        />
                    </InputGroup>
                </FormGroup>

                <FormHeader>{language.service.specialism}</FormHeader>

                <FormGroup hasErrors={errors.has('specialismCode')}>
                    <FormLabel>{language.service.specialism}</FormLabel>
                    <InputGroup>
                        <Dropdown
                            error={errors.get('specialismCode')}
                            onChange={(value) => handleDropDownChange('specialismCode', value)}
                            selectOptions={getSpecialismSelectOptions(specialisms)}
                            value={service.specialismCode}
                        />
                    </InputGroup>
                </FormGroup>

                <FormHeader>{language.service.customControl}</FormHeader>

                <FormGroup hasErrors={errors.has('customControlVersion')}>
                    <FormLabel isRequired>{language.service.customControlVersion}</FormLabel>
                    <InputGroup>
                        <InputNumber
                            error={errors.get('customControlVersion')}
                            onChange={(value) => handleChange('customControlVersion', value)}
                            onDoValidate={() => validateOne('customControlVersion')}
                            placeholder={language.service.customControlVersion}
                            value={service.customControlVersion}
                        />
                    </InputGroup>
                </FormGroup>

                {service.code === ServiceCode.TCOH && (
                    <FormGroup hasErrors={errors.has('ksyosHolterAnalystOrganizationId')}>
                        <FormLabel>{language.organization.fallBackAnalysisOrganization}</FormLabel>
                        <InputGroup>
                            <OrganizationSelection
                                error={errors.get('ksyosHolterAnalystOrganizationId')}
                                onChange={async (value) =>
                                    handleDropDownChange('ksyosHolterAnalystOrganizationId', value || null)
                                }
                                showCity
                                value={service.ksyosHolterAnalystOrganizationId ?? undefined}
                            />
                        </InputGroup>
                    </FormGroup>
                )}

                <CancelButton
                    disabled={isSaving}
                    to="/services"
                />
                <SaveSubmit disabled={isSaving} />
            </FormContainer>
        </FixedWidthTemplate>
    );

    async function initialize() {
        await loadEpisodeCategories();

        if (isEditMode) {
            await loadServiceAndSpecialisms();
        } else {
            await loadSpecialisms();

            setService({
                authorization: { canUpdateSupervisionPercentage: true },
                code: null,
                contractFunding: false,
                customControlVersion: 1,
                doNotDeclareSet: false,
                episodeCategoryId: null,
                isActive: true,
                isFundable: false,
                isStandaloneGraderNotAllowed: false,
                isSuppressNotifications: false,
                isSuppressZorgmail: false,
                ksyosHolterAnalystOrganizationId: null,
                ledgerNumber: '',
                name: '',
                noGeneralPractitionerInvolved: false,
                runFundingAfterEachActivity: false,
                sendNotificationOnCloseSubsequentService: false,
                sendZorgmailOnCloseSubsequentService: false,
                serviceType: null,
                specialismCode: null,
                supervisionPercentage: null,
                useNewFundingSystem: false,
                vatRateGroup: VatRateGroup.exempt,
                zorgmailType: ServiceZorgmailType.Geen,
            });
        }
    }

    async function loadSpecialisms(loadedService?: IService) {
        const url = '/api/dhdThesaurus/specialisms';
        const result = await abortableFetch.fetchJson(url, 'GET');

        setSpecialisms(
            (result.json.items as ISpecialism[]).filter(
                (specialism) =>
                    specialism.isActive || (loadedService && specialism.code === loadedService.specialismCode)
            )
        );
    }

    async function loadEpisodeCategories() {
        const url = '/api/episodeCategories';
        const results = await abortableFetch.fetchJson(url, 'GET');

        setEpisodeCategories(results.json.items);
    }

    async function loadServiceAndSpecialisms() {
        const authorizationQuery = 'includeItemAuthorizations=canUpdateSupervisionPercentage';
        const url = `/api/services/${id}?${authorizationQuery}`;
        const result = await abortableFetch.fetchJson(url, 'GET');

        setService(result.json);
        loadSpecialisms(result.json);
    }

    function handleDropDownChange<K extends keyof IService>(name: K, value: IService[K]) {
        handleChange(name, value);
        validateOne(name, { ...service, [name]: value });
    }

    function handleChange<K extends keyof IService>(name: K, value: IService[K]) {
        setService((current) => ({ ...current, [name]: value }));
    }

    function validateOne(name: keyof IService, serviceToValidate = service) {
        setErrors(updateValidationErrorsForOne(serviceAddEditSchema, serviceToValidate, errors, name));
    }

    function getEpisodeCategorySelectOptions() {
        const options = [
            { text: formatString(language.common.selectItem, [language.episodeCategory.episodeCategory]), value: '' },
        ];

        return options.concat(
            episodeCategories.map((episodeCategory) => {
                return { text: language.episodeCategoryEnum[episodeCategory.name], value: episodeCategory.id };
            })
        );
    }

    async function handleSave() {
        const url = `/api/services${id ? `/${id}` : ''}`;
        const { errors: validationErrors, item } = validate(serviceAddEditSchema, service);

        setErrors(validationErrors);

        if (validationErrors.size > 0) {
            return;
        }

        setIsSaving(true);
        await abortableFetch.fetchJson(url, 'POST', item);
        navigate(-1);
    }

    function isExamination() {
        return service && service.serviceType === ServiceType.examination;
    }

    function canUpdateSupervisionPercentage() {
        return Boolean(service?.authorization?.canUpdateSupervisionPercentage);
    }
}
